import { Resend } from 'resend';
import { AuditResult } from '../types';
import { UserNotificationBatch } from '../types';

const resend = new Resend(import.meta.env.VITE_RESEND_API_KEY || 're_placeholder');

export const emailService = {
  async sendAuditReport(email: string, result: AuditResult): Promise<{ success: boolean; message: string; html?: string; isSandboxRestriction?: boolean }> {
    if (typeof window !== 'undefined') {
      try {
        const response = await fetch('/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, result }),
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
          return {
            success: false,
            message: resData.message || `HTTP error ${response.status}`,
            html: resData.html
          };
        }
        if (!resData.success && resData.isSandboxRestriction) {
          console.warn('Email send hit sandbox restriction limits, returning HTML for preview.');
          return {
            success: false,
            isSandboxRestriction: true,
            message: resData.message || 'Sandbox restriction',
            html: resData.html
          };
        }
        console.log('Email sent successfully via serverless API proxy.');
        return {
          success: true,
          message: resData.message || 'Email sent successfully.',
          html: resData.html
        };
      } catch (error) {
        console.error('Failed to send email via serverless API proxy:', error);
        return {
          success: false,
          message: error instanceof Error ? error.message : String(error)
        };
      }
    }

    if (!import.meta.env.VITE_RESEND_API_KEY) {
      console.warn('Resend API Key missing. Email not sent.');
      return { success: false, message: 'Resend API Key missing.' };
    }

    const annualSavings = (result.potentialSavings * 12).toLocaleString();
    const origin = import.meta.env.VITE_APP_URL || 'https://vyaytarunya.vercel.app';
    const reportUrl = `${origin}/result/${result.publicId || result.id}`;

    const fromEmail = import.meta.env.VITE_FROM_EMAIL || 'onboarding@resend.dev';
    const fromSender = `Vyay Audit <${fromEmail}>`;

    const emailHtml = `
      <div style="font-family: serif; max-width: 600px; margin: 0 auto; padding: 40px; color: #1a1a1a; background: #fff;">
        <h1 style="font-style: italic; border-bottom: 1px solid #eee; padding-bottom: 20px;">Vyay Strategic Audit</h1>
        <p style="font-size: 18px; line-height: 1.6;">
          We have finalized your AI infrastructure audit. Our deterministic engine has identified 
          <strong>$${annualSavings}</strong> in potential annualized capital recovery.
        </p>
        <div style="background: #f9f9f9; padding: 20px; border-radius: 8px; margin: 30px 0;">
          <h2 style="margin-top: 0;">Executive Summary</h2>
          <p style="font-style: italic;">"${result.aiSummary || 'Analysis complete.'}"</p>
        </div>
        <p>You can access your full interactive report and implementation roadmap here:</p>
        <a href="${reportUrl}" style="display: inline-block; background: #000; color: #fff; padding: 15px 30px; text-decoration: none; border-radius: 5px; margin: 20px 0;">View Full Audit Report</a>
        <p style="color: #666; font-size: 14px; margin-top: 40px;">
          This report was generated using Vyay's deterministic audit engine. Confidence scores reflect market-standard pricing tiers as of May 2026.
        </p>
      </div>
    `;

    try {
      const { error } = await resend.emails.send({
        from: fromSender,
        to: email,
        subject: `Your AI Spend Audit: $${annualSavings}/year Optimization Potential`,
        html: emailHtml,
      });
      if (error) {
        console.error('[emailService] Resend API Error (report):', error);
        const isSandboxRestriction = 
          error.message.toLowerCase().includes('not verified') || 
          error.message.toLowerCase().includes('testing emails') ||
          error.statusCode === 403;
        return {
          success: false,
          isSandboxRestriction,
          message: error.message,
          html: emailHtml
        };
      }
      return { success: true, message: 'Email sent successfully.', html: emailHtml };
    } catch (error) {
      console.error('Failed to send email:', error);
      return { success: false, message: error instanceof Error ? error.message : String(error) };
    }
  },

  /**
   * Sends one consolidated stale-audit notification per user batch.
   *
   * Tone: specific, operational, non-hype.
   * Content: what changed, which tools, savings delta, re-audit link.
   */
  async sendStaleAuditNotification(batch: UserNotificationBatch): Promise<boolean> {
    if (!import.meta.env.VITE_RESEND_API_KEY) {
      console.warn('Resend API Key missing. Stale notification not sent.');
      return false;
    }

    const { userEmail, affectedAudits, changedTools, combinedDeltaMonthlySavings } = batch;

    // Build subject using specific tool names (max 2)
    const toolNames = [...new Set(changedTools.map(t => t.toolName))].slice(0, 2).join(', ');
    const subject = toolNames
      ? `Vyay: pricing update on ${toolNames} affected your audit`
      : `Vyay: a pricing change affected your previous audit`;

    // Delta direction
    const sign = combinedDeltaMonthlySavings >= 0 ? '+' : '';
    const deltaMonthlyStr = `${sign}$${Math.abs(combinedDeltaMonthlySavings).toFixed(0)}/month`;
    const deltaAnnualStr = `${sign}$${Math.abs(combinedDeltaMonthlySavings * 12).toFixed(0)}/year`;

    // Build changed tools list
    const toolRows = changedTools
      .map(c => {
        const oldStr = c.oldPrice !== null ? `$${c.oldPrice}/month` : 'custom pricing';
        const newStr = c.newPrice !== null ? `$${c.newPrice}/month` : 'custom pricing';
        return `<tr>
          <td style="padding: 8px 0; border-bottom: 1px solid #f0f0f0;">${c.toolName} - ${c.planLabel}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #f0f0f0; color: #999;">${oldStr}</td>
          <td style="padding: 8px 0; border-bottom: 1px solid #f0f0f0; color: #1a1a1a; font-weight: 600;">${newStr}</td>
        </tr>`;
      })
      .join('');

    // Build audit links
    const auditLinks = affectedAudits
      .map(audit => {
        if (!audit.publicId) return '';
        const origin = typeof window !== 'undefined'
          ? window.location.origin
          : (import.meta.env.VITE_APP_URL || 'https://vyaytarunya.vercel.app');
        const reauditUrl = `${origin}/re-audit/${audit.publicId}`;
        const oldSavings = `$${audit.oldMonthlySavings.toFixed(0)}/month`;
        const newSavings = `$${audit.newMonthlySavings.toFixed(0)}/month`;
        const sign2 = audit.deltaMonthlySavings >= 0 ? '+' : '';
        const delta = `${sign2}$${audit.deltaMonthlySavings.toFixed(0)}/month`;
        return `<div style="padding: 16px; border: 1px solid #e8e8e8; border-radius: 8px; margin: 12px 0;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
            <span style="font-size: 12px; color: #999; letter-spacing: 0.1em; text-transform: uppercase;">Audit ${audit.publicId.substring(0, 8).toUpperCase()}</span>
            <span style="font-size: 12px; font-weight: 600; color: ${audit.deltaMonthlySavings >= 0 ? '#16a34a' : '#dc2626'};">${delta}</span>
          </div>
          <div style="font-size: 13px; color: #666; margin-bottom: 12px;">
            Previous savings: ${oldSavings} &rarr; Updated: ${newSavings}
          </div>
          <a href="${reauditUrl}" style="font-size: 13px; color: #1a1a1a; font-weight: 600; text-decoration: underline;">
            View comparison &rarr;
          </a>
        </div>`;
      })
      .join('');

    const html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 600px; margin: 0 auto; padding: 40px 24px; color: #1a1a1a; background: #fff;">
        
        <!-- Header -->
        <div style="border-bottom: 1px solid #eee; padding-bottom: 24px; margin-bottom: 32px;">
          <span style="font-family: Georgia, serif; font-style: italic; font-size: 22px; letter-spacing: -0.03em;">Vyay</span>
        </div>

        <!-- Main message -->
        <h1 style="font-size: 20px; font-weight: 600; margin: 0 0 12px 0; line-height: 1.4;">
          Pricing change detected - your audit may need review.
        </h1>
        <p style="font-size: 15px; color: #555; line-height: 1.6; margin: 0 0 32px 0;">
          Our system detected vendor pricing updates that materially affect ${affectedAudits.length > 1 ? `${affectedAudits.length} of your previous audits` : 'one of your previous audits'}.
          The combined savings estimate has shifted by <strong>${deltaMonthlyStr}</strong> (${deltaAnnualStr} annualized).
        </p>

        <!-- Pricing changes table -->
        ${changedTools.length > 0 ? `
        <div style="margin-bottom: 32px;">
          <p style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.15em; color: #999; margin: 0 0 12px 0;">What changed</p>
          <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <thead>
              <tr>
                <th style="text-align: left; padding: 8px 0; border-bottom: 2px solid #e8e8e8; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #999;">Tool / Plan</th>
                <th style="text-align: left; padding: 8px 12px; border-bottom: 2px solid #e8e8e8; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #999;">Previous</th>
                <th style="text-align: left; padding: 8px 0; border-bottom: 2px solid #e8e8e8; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #999;">Current</th>
              </tr>
            </thead>
            <tbody>${toolRows}</tbody>
          </table>
        </div>
        ` : ''}

        <!-- Affected audits -->
        <div style="margin-bottom: 32px;">
          <p style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.15em; color: #999; margin: 0 0 12px 0;">Affected audits</p>
          ${auditLinks}
        </div>

        <!-- Footer -->
        <div style="border-top: 1px solid #eee; padding-top: 24px; font-size: 12px; color: #aaa; line-height: 1.6;">
          You received this because you previously requested an audit report from Vyay.
          Prices shown reflect vendor-published rates as of the detection date.
        </div>
      </div>
    `;

    const fromEmail = import.meta.env.VITE_FROM_EMAIL || 'onboarding@resend.dev';
    const fromSender = `Vyay Audit <${fromEmail}>`;

    try {
      const { error } = await resend.emails.send({
        from: fromSender,
        to: userEmail,
        subject,
        html,
      });
      if (error) {
        console.error(`[emailService] Resend API Error (stale):`, error);
        return false;
      }
      console.log(`[emailService] Stale audit notification sent to ${userEmail}`);
      return true;
    } catch (error) {
      console.error(`[emailService] Failed to send stale notification to ${userEmail}:`, error);
      return false;
    }
  },
};
